import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMentorContext } from '../contexts/MentorContext';

/**
 * MentorDashboardPage — landing page for the mentor role.
 *
 * Displays:
 * - Applicant display name and granted scope categories (R2.1)
 * - Suggestion counts: open, accepted, dismissed (R2.2)
 * - Navigation cards per granted scope category (R2.3)
 * - Inactive message when link status ≠ active (R2.4)
 * - Loading indicator while fetching (R2.7)
 * - Error message + retry if fetch fails (R2.6)
 *
 * Data is consumed from MentorContext (fetched by MentorLayout via
 * GET /api/mentors/dashboard — R2.5).
 *
 * Requirements: 2.1–2.7
 */

/** localStorage key for welcome banner dismissal (R9.5) */
export const WELCOME_DISMISSED_KEY = 'mentor_welcome_dismissed';

/** Human-friendly labels for scope categories */
const SCOPE_LABELS: Record<string, string> = {
  profile: 'Knowledge Base',
  cvs: 'CVs',
  cover_letters: 'Cover Letters',
  tracker: 'Application Tracker',
  analytics: 'Analytics',
};

/** Navigation card config per scope category */
interface NavCard {
  scope: string;
  icon: string;
  title: string;
  description: string;
  to: string;
  enabled: boolean;
}

function getNavCards(grantedScope: string[]): NavCard[] {
  const allCards: NavCard[] = [
    {
      scope: 'profile',
      icon: '📚',
      title: 'Knowledge Base',
      description: 'Review KB entries and leave suggestions',
      to: '/mentor/kb',
      enabled: true,
    },
    {
      scope: 'cvs',
      icon: '📄',
      title: 'Documents — CVs',
      description: 'View generated CVs and provide feedback',
      to: '/mentor/documents',
      enabled: true,
    },
    {
      scope: 'cover_letters',
      icon: '✉️',
      title: 'Documents — Cover Letters',
      description: 'View generated cover letters and provide feedback',
      to: '/mentor/documents',
      enabled: true,
    },
    {
      scope: 'tracker',
      icon: '📋',
      title: 'Application Tracker',
      description: 'View application progress',
      to: '#',
      enabled: false,
    },
    {
      scope: 'analytics',
      icon: '📊',
      title: 'Analytics',
      description: 'View application analytics',
      to: '#',
      enabled: false,
    },
  ];

  return allCards.filter((card) => grantedScope.includes(card.scope));
}

export default function MentorDashboardPage() {
  const { dashboard, loading, error, refetch } = useMentorContext();
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try {
      return localStorage.getItem(WELCOME_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleDismissWelcome = () => {
    try {
      localStorage.setItem(WELCOME_DISMISSED_KEY, 'true');
    } catch {
      // localStorage unavailable — dismiss for current session only
    }
    setWelcomeDismissed(true);
  };

  // R2.7: Loading indicator
  if (loading) {
    return (
      <div aria-label="Loading dashboard">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-4 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="h-24 bg-gray-200 rounded" />
            <div className="h-24 bg-gray-200 rounded" />
            <div className="h-24 bg-gray-200 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // R2.6: Error state
  if (error) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Mentor Dashboard</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <p className="text-red-700">{error}</p>
          <button
            onClick={refetch}
            className="mt-3 text-sm font-medium text-red-700 hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // R2.4, R9.2, R9.3: Inactive state — link status ≠ active
  if (dashboard && dashboard.status !== 'active') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Mentor Dashboard</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" role="status">
          <p className="text-amber-800 font-medium">
            {dashboard.status === 'invited' && 'Your mentor invitation has not yet been activated.'}
            {dashboard.status === 'revoked' && 'Your mentoring access has been removed.'}
            {dashboard.status === 'no_link_found' && 'No mentoring relationship found.'}
          </p>
          {dashboard.message && (
            <p className="text-amber-700 text-sm mt-1">{dashboard.message}</p>
          )}
        </div>
      </div>
    );
  }

  // Active state
  if (!dashboard) return null;

  const navCards = getNavCards(dashboard.scope);

  return (
    <div>
      {/* R9.1: Welcome banner for first-time mentors (dismissible, R9.5) */}
      {!welcomeDismissed && (
        <WelcomeBanner
          applicantName={dashboard.applicant_display_name}
          scope={dashboard.scope}
          onDismiss={handleDismissWelcome}
        />
      )}

      {/* R2.1: Applicant name and scope categories */}
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Mentor Dashboard</h1>
      {dashboard.applicant_display_name && (
        <p className="text-gray-600 mb-6">
          Mentoring <span className="font-medium text-gray-800">{dashboard.applicant_display_name}</span>
        </p>
      )}

      {/* Scope summary */}
      <div className="mb-6">
        <p className="text-sm text-gray-500 mb-2">Granted access</p>
        <div className="flex flex-wrap gap-2">
          {dashboard.scope.map((cat) => (
            <span
              key={cat}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {SCOPE_LABELS[cat] ?? cat}
            </span>
          ))}
        </div>
      </div>

      {/* R2.2: Suggestion counts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <SuggestionCard label="Open" count={dashboard.suggestion_counts.open} color="blue" />
        <SuggestionCard label="Accepted" count={dashboard.suggestion_counts.accepted} color="green" />
        <SuggestionCard label="Dismissed" count={dashboard.suggestion_counts.dismissed} color="gray" />
      </div>

      {/* R2.3: Navigation cards per granted scope category */}
      {navCards.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick access</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {navCards.map((card) =>
              card.enabled ? (
                <Link
                  key={card.scope}
                  to={card.to}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all block"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl" aria-hidden="true">{card.icon}</span>
                    <div>
                      <h3 className="font-medium text-gray-900">{card.title}</h3>
                      <p className="text-sm text-gray-500">{card.description}</p>
                    </div>
                  </div>
                </Link>
              ) : (
                <div
                  key={card.scope}
                  className="bg-gray-50 rounded-lg border border-gray-200 p-4 opacity-60 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl" aria-hidden="true">{card.icon}</span>
                    <div>
                      <h3 className="font-medium text-gray-500">{card.title}</h3>
                      <p className="text-sm text-gray-400">{card.description}</p>
                      <p className="text-xs text-gray-400 mt-1">Coming soon</p>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** R9.1: Welcome banner displayed on first visit for active mentors */
function WelcomeBanner({
  applicantName,
  scope,
  onDismiss,
}: {
  applicantName: string | null;
  scope: string[];
  onDismiss: () => void;
}) {
  return (
    <div
      className="mb-6 rounded-lg border border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 p-6"
      role="banner"
      aria-label="Welcome banner"
    >
      <h2 className="text-xl font-bold text-green-900 mb-2">
        Welcome! You're now mentoring{applicantName ? ` ${applicantName}` : ''}.
      </h2>
      <p className="text-green-800 mb-3">
        You have access to the following areas:
      </p>
      <ul className="list-disc list-inside text-green-700 mb-4 space-y-1">
        {scope.map((cat) => (
          <li key={cat}>{SCOPE_LABELS[cat] ?? cat}</li>
        ))}
      </ul>
      <button
        onClick={onDismiss}
        className="inline-flex items-center px-4 py-2 rounded-md bg-green-700 text-white text-sm font-medium hover:bg-green-800 transition-colors"
      >
        Got it
      </button>
    </div>
  );
}

/** Displays a suggestion count with a colored accent */
function SuggestionCard({ label, count, color }: { label: string; count: number; color: 'blue' | 'green' | 'gray' }) {
  const colorMap = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    gray: 'border-gray-200 bg-gray-50',
  };
  const textColorMap = {
    blue: 'text-blue-700',
    green: 'text-green-700',
    gray: 'text-gray-700',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorMap[color]}`}>
      <p className="text-sm text-gray-600">{label} suggestions</p>
      <p className={`text-2xl font-bold mt-1 ${textColorMap[color]}`}>{count}</p>
    </div>
  );
}
